/**
 * Company Model & Utilities
 * Manages multiple business entities/branches with per-company invoice numbering sequences.
 */

export function createDefaultCompany() {
  return {
    name: "",
    tradeName: "",
    gstin: "",
    phone: "",
    email: "",
    addressLine1: "",
    city: "",
    state: "Tamil Nadu",
    stateCode: "33",
    pincode: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    branchName: "",
    // GST Invoice Number Sequence
    invoicePrefix: "",              // Default empty per user requirement (can be set to 'INV-', 'SRT-', etc.)
    nextInvoiceNumber: 1,           // Default count starts from 1
    // Non-GST / Bill of Supply Number Sequence
    nonGstInvoicePrefix: "",        // Default empty (can be set to 'BOS-', 'EST-', etc.)
    nonGstNextInvoiceNumber: 1,     // Default count starts from 1
    invoiceCopies: 2,               // Default 2 copies: Original & Duplicate
    defaultTemplate: "modern-tax",  // 'modern-tax' (Default modern tax invoice) | 'paper-ledger' | 'compact'
    purchaseTemplate: "compact",    // Purchase bill PDF: 'compact' (A5 slip) | 'standard' (A4)
    dateFormat: "DD-MM-YYYY",       // Default DD-MM-YYYY (e.g. 17-09-2026)
    signatureUrl: "",               // Base64 or URL of authorized signature image
    bankAccounts: [],               // [{ id, bankName, accountNumber, ifscCode, branchName, accountHolder, isPrimary }]
    logoUrl: "",                    // 512×512 PNG data URL; used as the installed app icon (default company)
    appShortName: "",               // Optional name under the home-screen icon (max 12 chars)
    isDefault: false
  };
}

export const INVOICE_COPY_OPTIONS = [
  { count: 1, label: "1 Copy (Original Only)" },
  { count: 2, label: "2 Copies (Original + Duplicate)" },
  { count: 3, label: "3 Copies (Original + Duplicate + Triplicate)" }
];

import { getAllInvoiceTemplates } from "../../invoices/templates/registry.js";

export const INVOICE_TEMPLATES = getAllInvoiceTemplates();


export { DATE_FORMAT_OPTIONS, DEFAULT_DATE_FORMAT } from "../../../shared/utils/dateUtils.js";

/**
 * Formats the next invoice number based on company prefix and sequence
 * Supports separate sequences for GST vs Non-GST
 */
export function formatInvoiceNumber(company, isNonGst = false) {
  if (!company) return "1";
  if (isNonGst) {
    const prefix = company.nonGstInvoicePrefix !== undefined && company.nonGstInvoicePrefix !== null
      ? company.nonGstInvoicePrefix
      : "";
    const nextNumber = (company.nonGstNextInvoiceNumber !== undefined && company.nonGstNextInvoiceNumber !== null && company.nonGstNextInvoiceNumber !== "")
      ? company.nonGstNextInvoiceNumber
      : 1;
    return `${prefix}${nextNumber}`;
  }
  const prefix = company.invoicePrefix || "";
  const nextNumber = (company.nextInvoiceNumber !== undefined && company.nextInvoiceNumber !== null && company.nextInvoiceNumber !== "")
    ? company.nextInvoiceNumber
    : 1;
  return `${prefix}${nextNumber}`;
}


/* ------------------------------------------------------------------ */
/* Bank accounts (several per company; the primary prints on invoices) */
/* ------------------------------------------------------------------ */

export function newBankAccountId() {
  return `ba_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Accounts for a company; older records with only single bank fields become one primary account. */
export function getBankAccounts(company) {
  if (Array.isArray(company?.bankAccounts) && company.bankAccounts.length) return company.bankAccounts;
  if (company?.bankName || company?.accountNumber) {
    return [{
      id: `legacy_${company.id || "company"}`,
      bankName: company.bankName || "",
      accountNumber: company.accountNumber || "",
      ifscCode: company.ifscCode || "",
      branchName: company.branchName || "",
      accountHolder: company.name || "",
      isPrimary: true
    }];
  }
  return [];
}

/** Cleans a list: trims fields, drops empty rows, guarantees exactly one primary. */
export function normalizeBankAccounts(list = []) {
  const cleaned = list
    .map((a) => ({
      id: a.id || newBankAccountId(),
      bankName: String(a.bankName || "").trim(),
      accountNumber: String(a.accountNumber || "").replace(/\s+/g, ""),
      ifscCode: String(a.ifscCode || "").trim().toUpperCase(),
      branchName: String(a.branchName || "").trim(),
      accountHolder: String(a.accountHolder || "").trim(),
      isPrimary: !!a.isPrimary
    }))
    .filter((a) => a.bankName || a.accountNumber);
  if (cleaned.length && !cleaned.some((a) => a.isPrimary)) cleaned[0].isPrimary = true;
  let seenPrimary = false;
  return cleaned.map((a) => {
    const isPrimary = a.isPrimary && !seenPrimary;
    if (isPrimary) seenPrimary = true;
    return { ...a, isPrimary };
  });
}

/** Legacy single-account fields mirroring the primary account (used by invoices, receipts, backups). */
export function primaryBankFields(list = []) {
  const primary = list.find((a) => a.isPrimary) || list[0];
  return {
    bankName: primary?.bankName || "",
    accountNumber: primary?.accountNumber || "",
    ifscCode: primary?.ifscCode || "",
    branchName: primary?.branchName || ""
  };
}

export function maskAccountNumber(number = "") {
  const digits = String(number).replace(/\s+/g, "");
  return digits.length > 4 ? `••${digits.slice(-4)}` : digits;
}
